import {
    addToDateWithFormat,
    convertDateToUtc,
    getDateDifference,
    isDateBetween,
    subtractFromDateWithFormat
} from '../../utils/dates/date.utils';

import { INCOME_TYPES } from '@constants/ssap/hhmTypes/incomeAndExpenceTypes';
import { INLINE_ELIGIBILITY_VALUES } from '../../constants/ssap/miscellaneous/miscellaneous';
import { PAGES } from '../../constants/ssap/flows/pagesTypes';
import { DATE_FORMAT2 } from '@utils/dates/dateMethods';
import { OTHER_PROGRAMS_TYPES } from '@constants/ssap/hhmTypes/otherProgramsList';
import { IEX_STATE_CODES, STATE_NAMES } from '@constants/configs/states';

const {
    NO,
    NOT_APPLICABLE,
    YES
} = INLINE_ELIGIBILITY_VALUES;

const areChildrenHasHealthCoverageShown = params => {

    const {
        alwaysDisplayMedicaidQuestions,
        familyStatus,
        household,
        householdMembers,
        inlineCurrentIncomeEnabled,
        personId,
        preliminaryMedicaidStatus
    } = params;

    if(!familyStatus.relatedPersonsIds.children ||  household.primaryTaxFilerPersonId !== personId) {
        return {
            areChildrenShown: false,
            filteredChildren: []
        };
    }

    const filteredChildren = familyStatus.relatedPersonsIds.children.filter(ch => {
        const inlineEligibilityCriteria = [
            preliminaryMedicaidStatus
        ];

        if(
            alwaysDisplayMedicaidQuestions !== true &&
            inlineCurrentIncomeEnabled &&
            inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)
        ) {
            return !householdMembers[ch - 1].applyingForCoverageIndicator &&
                preliminaryMedicaidStatus === YES;
        } else {
            return !householdMembers[ch - 1].applyingForCoverageIndicator;
        }
    });

    return {
        areChildrenShown: filteredChildren.length > 0,
        filteredChildren
    };
};

const isMedicaidChipDenialShown = params => {

    const {
        hhm,
        medicaidTerminationFeatureEnabled,
        serverDate,
        medicaidEndDateOverride
    } = params;
    const { applyingForCoverageIndicator } = hhm ?? {};

    if(!applyingForCoverageIndicator) {
        return false;
    }

    if(!medicaidTerminationFeatureEnabled) {
        return true;
    }

    const {
        medicaidTermination,
        medicaidChipExpirationDate,
        householdIncomeOrSizeChanged
    } = hhm;

    const futureThreshold = 60;
    const pastThreshold = medicaidEndDateOverride || 90;

    const isMedicaidChipExpirationDateWithin = isDateBetween(
        medicaidChipExpirationDate,
        subtractFromDateWithFormat(
            pastThreshold,
            'day',
            DATE_FORMAT2,
            convertDateToUtc(serverDate)
        ),
        addToDateWithFormat(
            futureThreshold,
            'day',
            DATE_FORMAT2,
            convertDateToUtc(serverDate)
        ),
        'days'
    );

    const conditions = [
        !medicaidTermination,
        medicaidTermination && !isMedicaidChipExpirationDateWithin,
        medicaidTermination && isMedicaidChipExpirationDateWithin && householdIncomeOrSizeChanged
    ];

    return conditions.some(condition => condition === true);
};

const isMemberWithAbsentParentShown = params => {

    const {
        alwaysDisplayMedicaidQuestions,
        applyingForCoverageIndicator,
        familyStatus,
        inlineCurrentIncomeEnabled,
        preliminaryEmergencyMedicaidStatus,
        preliminaryMedicaidStatus
    } = params;

    const isMemberTheChildWithoutParent = (()=> {
        const analyzeIfNotAllParents = value => !Array.isArray(value) ? true : value.length < 2;
        return applyingForCoverageIndicator && !familyStatus.currentHhm.isOlderThanChildAge && analyzeIfNotAllParents(familyStatus.relatedPersonsIds.parents);
    })();

    const inlineEligibilityCriteria = [
        preliminaryMedicaidStatus,
        preliminaryEmergencyMedicaidStatus
    ];

    if (
        alwaysDisplayMedicaidQuestions !== true &&
        inlineCurrentIncomeEnabled &&
        inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)
    ) {
        return isMemberTheChildWithoutParent && inlineEligibilityCriteria.some(v => v === YES);
    } else {
        return isMemberTheChildWithoutParent;
    }
};

const isMcoSelectionShown = params => {
    const {
        alwaysDisplayMedicaidQuestions,
        inlineCurrentIncomeEnabled,
        isState,
        preliminaryMedicaidStatus
    } = params;

    const inlineEligibilityCriteria = [
        preliminaryMedicaidStatus
    ];

    if(!isState.NM) {
        return false;
    }

    if (
        alwaysDisplayMedicaidQuestions !== true &&
        inlineCurrentIncomeEnabled &&
        inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)
    ) {
        return inlineEligibilityCriteria.some(v => v === YES);
    } else {
        return inlineEligibilityCriteria.some(v => v === YES);
    }
};

const isLivingArrangementShown = params => {
    const {
        collectLivingArrangementEnabled,
        preliminaryMedicaidStatus
    } = params;

    return collectLivingArrangementEnabled && preliminaryMedicaidStatus === YES;
};

const isIndianHealthServicesShown = params => {

    const {
        alwaysDisplayMedicaidQuestions,
        applyingForCoverageIndicator,
        inlineCurrentIncomeEnabled,
        isItuQuestionsDisabled,
        memberOfFederallyRecognizedTribeIndicator,
        preliminaryChipStatus,
        preliminaryEmergencyMedicaidStatus,
        preliminaryMedicaidStatus,
    } = params;

    const inlineEligibilityCriteria = [
        preliminaryMedicaidStatus,
        preliminaryEmergencyMedicaidStatus,
        preliminaryChipStatus
    ];

    if (isItuQuestionsDisabled) {
        return false;
    }
    if (
        alwaysDisplayMedicaidQuestions !== true &&
        inlineCurrentIncomeEnabled &&
        inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)
    ) {
        return applyingForCoverageIndicator &&
            memberOfFederallyRecognizedTribeIndicator &&
            inlineEligibilityCriteria.some(v => v === YES);
    } else {
        return applyingForCoverageIndicator && memberOfFederallyRecognizedTribeIndicator;
    }
};

const isRetroactiveAssistanceShown = params => {

    const {
        alwaysDisplayMedicaidQuestions,
        applyingForCoverageIndicator,
        inlineCurrentIncomeEnabled,
        preliminaryEmergencyMedicaidStatus,
        preliminaryMedicaidStatus
    } = params;

    const inlineEligibilityCriteria = [
        preliminaryMedicaidStatus,
        preliminaryEmergencyMedicaidStatus
    ];

    if (
        alwaysDisplayMedicaidQuestions !== true &&
        inlineCurrentIncomeEnabled &&
        inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)
    ) {
        return applyingForCoverageIndicator && inlineEligibilityCriteria.some(v => v === YES);
    } else {
        return applyingForCoverageIndicator;
    }
};

const isSogiShown = params => {
    const {
        hhm,
        isSogiFeatureEnabled,
        serverDate
    } = params;

    const isHhmOlderThan12 = getDateDifference(serverDate, hhm?.dateOfBirth, 'years', true) >= 12;

    const conditions = [
        isHhmOlderThan12,
        isSogiFeatureEnabled
    ];

    return conditions.every(condition => condition === true);
};

const isESCorHRAShown = (params) => {
    const { /* istanbul ignore next */
        currentOtherInsurance: {
            medicareEligibleIndicator,
            otherStateOrFederalPrograms,
            peaceCorpsIndicator,
            tricareEligibleIndicator
        } = {},
        checkHRA,
        hhm = {},
        hhmStatus,
        inlineCurrentIncomeEnabled,
        inlineEligibilityIgnoredForEscPage,
        preliminaryAptcCsrStatus,
        serverDate,
    } = params;

    const { COBRA_COVERAGE, RETIRE_HEALTH_BENEFITS, VETERANS_AFFAIRS_HCP } = OTHER_PROGRAMS_TYPES;
    const isOtherInsuranceEligible = otherStateOrFederalPrograms.some(({ eligible, type }) =>
        eligible && [COBRA_COVERAGE, RETIRE_HEALTH_BENEFITS, VETERANS_AFFAIRS_HCP].includes(type)
    );

    const shouldRender = [
        preliminaryAptcCsrStatus === YES,
        peaceCorpsIndicator === false,
        tricareEligibleIndicator === false,
        medicareEligibleIndicator === false,
        !isOtherInsuranceEligible
    ].every(condition => condition);

    // Check depending on inlineEligibilityIgnoredForEscPage setting is temporarily added
    // until the decision by Business is made: if we keep or remove updated visibility conditions
    const isBasicEligible = () => {
        const age = getDateDifference(serverDate, hhm.dateOfBirth, 'year');
        if (!hhm.applyingForCoverageIndicator) return false;
        if (age >= 14) return true;
        return hhmStatus.currentHhm[checkHRA ? 'hasParentOrCaretakersWithHRA' : 'hasParentOrCaretakersWithEsc'] === true;
    };

    const result = (!inlineEligibilityIgnoredForEscPage && inlineCurrentIncomeEnabled && preliminaryAptcCsrStatus !== NOT_APPLICABLE)
        ? isBasicEligible() && shouldRender
        : isBasicEligible();

    return result;
};

const isEmployeeStateHealthBenefitShown = (params) => {
    const {
        alwaysDisplayMedicaidQuestions,
        applyingForCoverageIndicator,
        inlineCurrentIncomeEnabled,
        preliminaryChipStatus,
        preliminaryEmergencyMedicaidStatus,
        preliminaryMedicaidStatus,
        showStateEmployeeHealthBenefitPlan,
        stateCode,
    } = params;

    //TODO: Once implemented from backend, we can remove state check and have only flag dependency
    if (!showStateEmployeeHealthBenefitPlan || stateCode === IEX_STATE_CODES.NM) {
        return false;
    }

    const inlineEligibilityCriteria = [
        preliminaryMedicaidStatus,
        preliminaryEmergencyMedicaidStatus,
        preliminaryChipStatus
    ];

    return (
        alwaysDisplayMedicaidQuestions !== true &&
        inlineCurrentIncomeEnabled &&
        inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)
    )
        ? inlineEligibilityCriteria.some(v => v === YES)
        : applyingForCoverageIndicator;
};

const isMedicalEmergencyShown = (params) => {
    const {
        alwaysDisplayMedicaidQuestions,
        applyingForCoverageIndicator,
        inlineCurrentIncomeEnabled,
        preliminaryEmergencyMedicaidStatus,
        preliminaryMedicaidStatus,
        stateCode,
        citizenshipStatusIndicator,
        naturalizedCitizenshipIndicator,
    } = params;

    if (stateCode !== IEX_STATE_CODES.MN) {
        return false;
    }

    // eligible for medicaid or emergency medicaid
    const inlineEligibilityCriteria = [
        preliminaryMedicaidStatus,
        preliminaryEmergencyMedicaidStatus
    ];

    // applying for coverage & not a US/Naturalized/Derived citizen
    const applyingAndNotCitizen = applyingForCoverageIndicator && citizenshipStatusIndicator === false && !naturalizedCitizenshipIndicator;

    return (
        alwaysDisplayMedicaidQuestions !== true &&
        inlineCurrentIncomeEnabled &&
        inlineEligibilityCriteria.every(v => v !== NOT_APPLICABLE)
    )
        ? inlineEligibilityCriteria.some(v => v === YES) && applyingAndNotCitizen 
        : applyingAndNotCitizen;

};

/**
 * Whether a household member is eligible to reconcile APTC — shared by the
 * "Reconciliation of APTC" page-skip logic (pageVisibility.reconciliationOfAptcPage)
 * and the per-member summary row (ReconcileAptcInfo).
 * Illinois: never shown, for all scenarios (HIX-231517).
 * itfFeatureEnabled: eligible if member is the Primary Tax Filer OR an Independent Tax Filer.
 * !itfFeatureEnabled: eligible only if member is the Primary Tax Filer.
 */
const isReconciliationOfAptcShown = params => {
    const {
        independentTaxFiler,
        isState,
        itfFeatureEnabled,
        personId,
        primaryTaxFilerPersonId
    } = params;

    if(isState.IL) {
        return false;
    }

    const isPTF = primaryTaxFilerPersonId === personId;

    return itfFeatureEnabled
        ? isPTF || independentTaxFiler
        : isPTF;
};

const isFullTimeStudentShown = params => {
    const {
        applyingForCoverageIndicator,
        dateOfBirth,
        serverDate,
        showFullTimeStudentEnabled
    } = params;

    if (!showFullTimeStudentEnabled) return false;

    const age = getDateDifference(serverDate, dateOfBirth, 'year');
    return age >= 18 && age <= 22 && applyingForCoverageIndicator;
};

/**
 * Q1 + Q2 eligibility: citizenship/immigration criteria.
 * NonMN — existing Scenario A: member did not live in US since 1996.
 * MN    — member must be seeking coverage in a financial-assistance application,
 *         have lawful presence, not have lived in US since 1996, and have unchanged
 *         immigration status in the last five years.
 */
export const isMilitaryServiceCitizenshipImmigrationCriteriaMet = ({ hhm, isState, applyingForFinancialAssistanceIndicator }) => {
    const {
        applyingForCoverageIndicator,
        citizenshipImmigrationStatus: {
            lawfulPresenceIndicator,
            livedIntheUSSince1996Indicator,
            unchangedImmigrationStatusLastFiveYears
        }
    } = hhm;

    if (!isState.MN) {
        // NonMN: unchanged Scenario A
        return livedIntheUSSince1996Indicator === false;
    }

    // MN: member must be applying for coverage in a financial-assistance application
    //     AND meet all three citizenship/immigration conditions
    return applyingForFinancialAssistanceIndicator === true
        && applyingForCoverageIndicator === true
        && lawfulPresenceIndicator === true
        && livedIntheUSSince1996Indicator === false
        && unchangedImmigrationStatusLastFiveYears === false;
};

/**
 * Q3 eligibility: coverage criteria (MN only).
 * NonMN — always false; Q3 does not exist for non-MN states.
 * MN    — member must be seeking coverage.
 */
export const isMilitaryServiceCoverageCriteriaMet = ({ hhm, isState }) => {
    if (!isState.MN) return false;
    return hhm.applyingForCoverageIndicator === true;
};

export const questionsVisibility = {
    areChildrenHasHealthCoverageShown,
    isIndianHealthServicesShown,
    isLivingArrangementShown,
    isMedicaidChipDenialShown,
    isMemberWithAbsentParentShown,
    isMilitaryServiceCitizenshipImmigrationCriteriaMet,
    isMilitaryServiceCoverageCriteriaMet,
    isMcoSelectionShown,
    isReconciliationOfAptcShown,
    isRetroactiveAssistanceShown,
    isSogiShown,
    isEmployeeStateHealthBenefitShown,
    isESCorHRAShown,
    isFullTimeStudentShown,
    isMedicalEmergencyShown
};

/**
 * Page2_25 visibility — called by PagesContainer.
 * MN:    page visible when any member meets Q1/Q2 citizenship criteria OR Q3 coverage criteria (OR logic).
 * NonMN: page visible when any member meets citizenship criteria (livedInUS = false) — unchanged.
 */
const militaryServicePage = (params) => {
    const { householdMembers, isState } = params;

    const isPageVisible = householdMembers.some(hhm =>
        [
            isMilitaryServiceCitizenshipImmigrationCriteriaMet({ ...params, hhm }),
            isMilitaryServiceCoverageCriteriaMet({ ...params, hhm }),
        ].some(q => q === true),
    );

    return [
        !isPageVisible,
        [PAGES.ACTIVE_MILITARY_VETERAN_PAGE],
        !isPageVisible
            ? 'No member meets citizenship/immigration criteria (Q1/Q2) or coverage criteria (Q3)'
            : ''
    ];
};

const additionalInformationPage = params => {
    const {
        familyStatusForCurrentHhm,
        primaryTaxFilerPersonId
    } = params;

    const { areChildrenShown } = areChildrenHasHealthCoverageShown({
        ...params,
        household: { primaryTaxFilerPersonId },
        familyStatus: familyStatusForCurrentHhm
    });

    const payload = [
        isIndianHealthServicesShown(params),
        isRetroactiveAssistanceShown(params),
        isMemberWithAbsentParentShown({ ...params, familyStatus: familyStatusForCurrentHhm }),
        areChildrenShown,
        isMcoSelectionShown(params),
        isLivingArrangementShown(params),
    ];

    return [
        !payload.some(condition => condition),
        [
            PAGES.ADDITIONAL_INFORMATION_PAGE
        ],
        'person does not qualify for Indian Health Services, Retroactive Assistance, Absent Parent or Children with Health Coverage, MCO selection, Living Arrangement',
        // Next element in array is only for test purposes
        payload
    ];
};

const medicaidChipDenialInformationPage = params => {
    const {
        householdMembers,
        medicaidTerminationFeatureEnabled,
    } = params;
    if(!medicaidTerminationFeatureEnabled) {
        return [
            false,
            [
                PAGES.MEDICAID_CHIP_DENIAL_INFORMATION_PAGE
            ],
            'Medicaid termination feature is disabled'
        ];
    }

    const atLeastOneMemberHasMedicaidTermination = householdMembers.some(hhm =>
        isMedicaidChipDenialShown({
            ...params,
            hhm
        })
    );

    return [
        !atLeastOneMemberHasMedicaidTermination,
        [
            PAGES.MEDICAID_CHIP_DENIAL_INFORMATION_PAGE
        ],
        !atLeastOneMemberHasMedicaidTermination
            ? 'no member has Medicaid termination within the specified thresholds'
            : 'someone is qualified'
    ];
};

const stateEmployeeHealthBenefitPage = params => {

    const {
        inlineCurrentIncomeEnabled,
        showStateEmployeeHealthBenefitPlan,
        stateCode,
    } = params;

    const explanation = (!showStateEmployeeHealthBenefitPlan || stateCode === IEX_STATE_CODES.NM)
        ? `${STATE_NAMES[stateCode]} state wants this page to be hidden`
        : inlineCurrentIncomeEnabled
            ? `all three values 
                         "preliminaryMedicaidStatus",
                         "preliminaryChipStatus" and 
                         "preliminaryEmergencyMedicaidStatus" 
                         are equal to NO`
            : '- person does not apply for coverage';

    return [
        !isEmployeeStateHealthBenefitShown(params),
        [
            PAGES.STATE_EMPLOYEE_HEALTH_BENEFIT_PAGE
        ],
        explanation
    ];
};

const medicalEmergencyPage = params => {

    const {
        inlineCurrentIncomeEnabled,
        stateCode,
        citizenshipStatusIndicator,
        naturalizedCitizenshipIndicator,
    } = params;

    const explanation = (stateCode !== IEX_STATE_CODES.MN)
        ? `${STATE_NAMES[stateCode]} state wants this page to be hidden`
        : citizenshipStatusIndicator !== false || naturalizedCitizenshipIndicator
            ? 'not applicable for a person if US/Naturalized/Derived citizen'
            : inlineCurrentIncomeEnabled
                ? 'both values "preliminaryMedicaidStatus" and "preliminaryEmergencyMedicaidStatus" are equal to NO'
                : '- person does not apply for coverage';

    return [
        !isMedicalEmergencyShown(params),
        [
            PAGES.MEDICAL_EMERGENCY_STATUS_PAGE
        ],
        explanation
    ];
};

const fullTimeStudentPage = params => {
    const { householdMembers, showFullTimeStudentEnabled } = params;
    const anyFullTimeStudent = householdMembers.some(hhm => {
        return isFullTimeStudentShown({
            ...params,
            dateOfBirth: hhm.dateOfBirth,
            applyingForCoverageIndicator: hhm.applyingForCoverageIndicator,
        });
    });

    return [
        !anyFullTimeStudent,
        [
            PAGES.FULL_TIME_STUDENT_PAGE
        ],
        showFullTimeStudentEnabled
            ? '- no one HHM age of 18 - 22 y.o.'
            : '- page disabled by settings'
    ];
};

const checkEmployerCoverageDetailsPage = params => {
    const {
        inlineCurrentIncomeEnabled,
        inlineEligibilityIgnoredForEscPage,
    } = params;

    return [
        !isESCorHRAShown(params),
        [
            PAGES.EMPLOYER_COVERAGE_DETAIL_PAGE,
            PAGES.HEALTH_REIMBURSEMENT_ARRANGEMENT_DETAIL_PAGE
        ],
        inlineEligibilityIgnoredForEscPage || !inlineCurrentIncomeEnabled
            ? '- person does not match basic eligibility'
            : `person does not match basic eligibility OR
                        "preliminaryAptcCsrStatus" !== YES OR 
                        "peaceCorpsIndicator" !== false OR 
                        "tricareEligibleIndicator" !== false OR 
                        "medicareEligibleIndicator" !== false OR 
                        otherStateOrFederalPrograms contains eligible any of these programs COBRA_COVERAGE / RETIRE_HEALTH_BENEFITS / VETERANS_AFFAIRS_HCP`

    ];
};

/**
 * Page4_62 (Reconciliation of APTC) visibility.
 * Hidden when the household member is neither the Primary Tax Filer nor
 * (with itfFeatureEnabled) an Independent Tax Filer, and hidden entirely for Illinois.
 */
const reconciliationOfAptcPage = params => {
    const {
        isState,
        itfFeatureEnabled
    } = params;

    const isShown = isReconciliationOfAptcShown(params);

    const explanation = isState.IL
        ? '- Illinois hides Reconciliation of APTC page for all scenarios'
        : itfFeatureEnabled
            ? '- household member neither Primary Tax Filer nor Independent Tax Filer'
            : '- applicable only if hhm matches primaryTaxFilerPersonId';

    return [
        !isShown,
        [
            PAGES.RECONCILIATION_IN_APTC_PAGE
        ],
        explanation
    ];
};

const incomeDiscrepancyPage = params => {
    const {
        householdMembers,
        inlineCurrentIncomeEnabled,
        isState
    } = params;

    if(!inlineCurrentIncomeEnabled) {
        return [
            true,
            [
                PAGES.INCOME_DISCREPANCY_PAGE
            ],
            'Inline Current Income feature is not enabled'
        ];
    }

    const isJobIncomeDiscrepancy = !isState?.MN && householdMembers.some(hhm => {
        return hhm.incomes.some(
            income => (income.type === INCOME_TYPES.JOB || income.type === INCOME_TYPES.DUMMY)
                && !!income.jobIncomeExplanationRequiredIndicator
        );
    });

    const isTaxHouseholdIncomeDiscrepancy = householdMembers.some(hhm => {
        const {
            annualTaxIncome: {
                incomeExplanationRequiredIndicator
            }
        } = hhm;

        return !!incomeExplanationRequiredIndicator;
    });

    const isNoDiscrepancy = !isJobIncomeDiscrepancy && !isTaxHouseholdIncomeDiscrepancy;
    
    return [
        isNoDiscrepancy,
        [
            PAGES.INCOME_DISCREPANCY_PAGE
        ],
        isNoDiscrepancy ? 'No discrepancies found in member\'s tax household or member\'s levels' : null
    ];
};

export const pageVisibility = {
    additionalInformationPage,
    incomeDiscrepancyPage,
    medicaidChipDenialInformationPage,
    reconciliationOfAptcPage,
    stateEmployeeHealthBenefitPage,
    fullTimeStudentPage,
    checkEmployerCoverageDetailsPage,
    militaryServicePage,
    medicalEmergencyPage
};